import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Save, ArrowLeft, Search, Tag, Paperclip, History, Zap } from 'lucide-react';
import UniversalLookup from './UniversalLookup';
import AttachmentsModal from './AttachmentsModal';
import StatusHistoryModal from './StatusHistoryModal';

const TagoutDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [activeTab, setActiveTab] = useState('Tag');
  const [loading, setLoading] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [count, setCount] = useState(0);

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const [data, setData] = useState({
    tagoutnum: '', description: '', assetnum: '', location: '', required_state: 'OFF',
    siteid: session.site_id || '', orgid: session.org_id || ''
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '' });

  useEffect(() => {
    if (!isNew) axios.get(`/api/tagouts/${id}`).then(res => setData(res.data));
  }, [id, isNew]);

  const fetchCount = useCallback(async () => {
    if (!data.tagoutnum || isNew) return;
    try {
      const res = await axios.get(`/api/attachments/count/TAGOUT/${data.tagoutnum}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error(err); }
  }, [data.tagoutnum, isNew]);

  useEffect(() => { fetchCount(); }, [fetchCount]);

  const handleSave = async () => {
    try {
      setLoading(true);
      await axios.post('/api/tagouts/save', data);
      toast.success("Tagout Point Saved!");
      if (isNew) navigate(`/tagouts/${data.tagoutnum}`);
    } catch (err) { toast.error("Save failed"); } 
    finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col h-screen bg-[#f3f4f6] text-[11px] font-sans uppercase font-bold tracking-tighter">
      <UniversalLookup {...lookup} onClose={() => setLookup({...lookup, isOpen: false})} onSelect={(r) => setData({...data, [lookup.field]: r.value || r.assetnum || r.location})} />
      {!isNew && <AttachmentsModal isOpen={isDocModalOpen} onClose={() => {setIsDocModalOpen(false); fetchCount();}} ownertable="TAGOUT" ownerid={data.tagoutnum} />}
      {showHistoryModal && <StatusHistoryModal isOpen={showHistoryModal} appName="TAGOUT" recordId={data.tagoutnum} onClose={() => setShowHistoryModal(false)} />}

      <div className="bg-white p-2 border-b flex justify-between items-center shadow-sm z-20 font-black">
        <div className="flex gap-6 ml-4">
          <button onClick={() => navigate('/tagouts')} className="p-1 hover:bg-gray-100 rounded-full text-blue-900"><ArrowLeft size={18} /></button>
          <HeaderField label="Tag Num" value={data.tagoutnum} isBold width="w-32" disabled={!isNew} onChange={v => setData({...data, tagoutnum: v})} />
          <HeaderField label="Description" value={data.description} onChange={v => setData({...data, description: v})} width="w-[300px]" />
          <HeaderField label="Site" value={data.siteid} disabled width="w-24" />
        </div>
        <div className="flex gap-2 mr-4">
            <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 text-blue-900 rounded-sm hover:bg-blue-600 hover:text-white transition-all">
                <Paperclip size={18} />
                {count > 0 && <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center border border-white">{count}</span>}
            </button>
            <button onClick={() => setShowHistoryModal(true)} className="p-2 text-blue-900 hover:bg-blue-50 rounded"><History size={16}/></button>
            <button onClick={handleSave} disabled={loading} className="bg-[#3c70a4] text-white px-8 py-2 rounded flex items-center gap-2 hover:bg-[#1a3353] shadow-lg">
               <Save size={14} /> {loading ? 'SAVING...' : 'SAVE'}
            </button>
        </div>
      </div>

      <div className="flex bg-[#1a3353] text-white shadow-md">
        <button className="px-10 py-3 flex items-center gap-2 font-black bg-white text-[#1a3353]"><Zap size={14}/> Isolation Detail</button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 bg-[#f8f9fb]">
          <div className="max-w-4xl mx-auto space-y-6">
            <SectionCard title="Tagout / Lockout Point Information">
              <div className="grid grid-cols-2 gap-8 p-4">
                <EditableField label="Tag Number" value={data.tagoutnum} disabled={!isNew} />
                <EditableField label="Required State" value={data.required_state} onChange={v => setData({...data, required_state: v})} placeholder="e.g. CLOSED, OFF, LOCKED" />
                <EditableField label="Asset Number" value={data.assetnum} onSearch={() => setLookup({isOpen: true, title: 'Select Asset', type: 'assets', field: 'assetnum'})} />
                <EditableField label="Location" value={data.location} onSearch={() => setLookup({isOpen: true, title: 'Select Location', type: 'locations', field: 'location'})} />
                <div className="col-span-2"><EditableField label="Detailed Description" value={data.description} onChange={v => setData({...data, description: v})} /></div>
              </div>
            </SectionCard>
          </div>
      </div>
    </div>
  );
};

const HeaderField = ({ label, value, onChange, width, isBold, disabled }) => (
  <div className="flex flex-col border-r px-4 last:border-0">
    <label className="text-[8px] text-gray-400 font-black uppercase tracking-widest mb-0.5 italic">{label}</label>
    <input className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-blue-900 text-[12px]' : 'text-blue-700'} bg-transparent focus:border-blue-500`} 
           value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} />
  </div>
);

const SectionCard = ({ title, children }) => (
  <div className="bg-white border rounded shadow-sm overflow-hidden transition-all hover:shadow-md">
    <div className="p-2 bg-slate-50 border-b font-black text-slate-500 uppercase text-[9px] tracking-widest flex items-center gap-2">
      <div className="w-1.5 h-3 bg-blue-800 rounded-full"></div> {title}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const EditableField = ({ label, value, onChange, onSearch, type = "text", disabled }) => (
  <div className="flex flex-col gap-1 border-b border-gray-200 group focus-within:border-blue-500 transition-all py-1">
    <label className="text-gray-400 font-black text-[8px] uppercase tracking-widest italic">{label}</label>
    <div className="flex items-center justify-between pb-0.5">
      <input type={type} disabled={disabled} className="outline-none font-black text-blue-900 bg-transparent w-full text-[11px]" 
        value={value || ''} onChange={e => onChange && onChange(e.target.value)} />
      {onSearch && <Search size={12} className="text-blue-400 cursor-pointer hover:text-blue-800" onClick={onSearch} />}
    </div>
  </div>
);

export default TagoutDetails;
